import { useTranslation } from "react-i18next";

interface StatusBadgeProps {
  status: string;
  pulse?: boolean;
  size?: "sm" | "md";
}

const STATUS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  // Instance statuses
  provisioning: { bg: "bg-blue-500/10", text: "text-blue-400", dot: "bg-blue-400" },
  starting: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-400" },
  running: { bg: "bg-green-500/10", text: "text-green-400", dot: "bg-green-400" },
  stopping: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-400" },
  stopped: { bg: "bg-gray-500/10", text: "text-gray-400", dot: "bg-gray-400" },
  error: { bg: "bg-red-500/10", text: "text-red-400", dot: "bg-red-400" },
  deleting: { bg: "bg-red-500/10", text: "text-red-400", dot: "bg-red-400" },
  // User statuses
  pending: { bg: "bg-orange-500/10", text: "text-orange-400", dot: "bg-orange-400" },
  approved: { bg: "bg-green-500/10", text: "text-green-400", dot: "bg-green-400" },
  suspended: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-400" },
  banned: { bg: "bg-red-500/10", text: "text-red-400", dot: "bg-red-400" },
  // Roles
  admin: { bg: "bg-purple-500/10", text: "text-purple-400", dot: "bg-purple-400" },
  user: { bg: "bg-gray-500/10", text: "text-gray-400", dot: "bg-gray-400" },
  // Update statuses
  idle: { bg: "bg-gray-500/10", text: "text-gray-400", dot: "bg-gray-400" },
  downloading: { bg: "bg-blue-500/10", text: "text-blue-400", dot: "bg-blue-400" },
  updating: { bg: "bg-blue-500/10", text: "text-blue-400", dot: "bg-blue-400" },
  completed: { bg: "bg-green-500/10", text: "text-green-400", dot: "bg-green-400" },
  // Self-hosted statuses
  connecting: { bg: "bg-yellow-500/10", text: "text-yellow-400", dot: "bg-yellow-400" },
  active: { bg: "bg-green-500/10", text: "text-green-400", dot: "bg-green-400" },
  disconnected: { bg: "bg-red-500/10", text: "text-red-400", dot: "bg-red-400" },
  disabled: { bg: "bg-gray-500/10", text: "text-gray-400", dot: "bg-gray-400" },
};

const PULSING_STATUSES = new Set([
  "provisioning",
  "starting",
  "stopping",
  "deleting",
  "pending",
  "downloading",
  "updating",
  "connecting",
]);

export function StatusBadge({ status, pulse, size = "sm" }: StatusBadgeProps) {
  const { t } = useTranslation();
  const colors = STATUS_COLORS[status] ?? STATUS_COLORS.stopped;
  const shouldPulse = pulse ?? PULSING_STATUSES.has(status);

  const sizeClasses = size === "sm" ? "text-xs px-2 py-0.5" : "text-sm px-2.5 py-1";

  const statusKey = `dashboard.status.${status}` as const;
  const translatedStatus = t(statusKey, { defaultValue: status });

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-medium ${colors.bg} ${colors.text} ${sizeClasses}`}
    >
      <span className={`relative flex h-2 w-2`}>
        {shouldPulse && (
          <span
            className={`animate-ping absolute inline-flex h-full w-full rounded-full ${colors.dot} opacity-75`}
          />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${colors.dot}`} />
      </span>
      {translatedStatus}
    </span>
  );
}
